import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace, WORKSPACES } from "../../contexts/Workspacecontext";
import { useAuth } from "../../contexts/AuthContext";
import "./WorkspaceSelect.scss";

const COMPANY_ROLES = [
  "ROLE_ADMIN",
  "ROLE_RECRUITER-HR",
  "ROLE_MANAGER",
  "ROLE_HOD",
  "ROLE_EMPLOYEE",
];

const CONSULTANT_ROLES = [
  "ROLE_ADMIN",
  "ROLE_CONSULTANT",
  "ROLE_CONSULTANT-ADMIN",
];

const getUserRoles = (user) => {
  const roles = user?.roles || user?.authorities || user?.role || [];
  if (typeof roles === "string") return [roles];
  if (Array.isArray(roles)) {
    return roles.map((r) =>
      typeof r === "string" ? r : r.authority || r.role || r,
    );
  }
  return [];
};

const WorkspaceSelect = () => {
  const { setWorkspace } = useWorkspace();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const userRoles = getUserRoles(user);

  // Role checks
  const canAccessCompany = userRoles.some((r) => COMPANY_ROLES.includes(r));
  const canAccessConsultant = userRoles.some((r) =>
    CONSULTANT_ROLES.includes(r),
  );

  const hasCompanyModules = user?.moduleAccess?.some(
    (m) => m.moduleAccessID === "RECRUITMENT",
  );

  const hasConsultantModules = user?.moduleAccess?.some(
    (m) => m.moduleAccessID === "CONSULTANCY",
  );

  // Upgrade state: role passes but zero modules activated
  const isCompanyUpgrade = canAccessCompany && !hasCompanyModules;
  const isConsultantUpgrade = canAccessConsultant && !hasConsultantModules;

  const handleSelect = (ws) => {
    if (ws === WORKSPACES.COMPANY) {
      if (!canAccessCompany) {
        setError(
          "You don't have access to the Company workspace. Your role doesn't allow HR, Manager, or HOD workflows.",
        );
        return;
      }
      if (!hasCompanyModules) {
        window.location.href = "https://ourdept.com";
        return;
      }
    }

    if (ws === WORKSPACES.CONSULTANT) {
      if (!canAccessConsultant) {
        setError(
          "You don't have access to the Consultancy workspace. Your role doesn't allow consultancy workflows.",
        );
        return;
      }
      if (!hasConsultantModules) {
        window.location.href = "https://ourdept.com";
        return;
      }
    }

    setError("");
    setWorkspace(ws);
    navigate("/dashboard");
  };

  const isCompanyDisabled = !canAccessCompany;
  const isConsultantDisabled = !canAccessConsultant;
  const onlyOneAllowed = canAccessCompany !== canAccessConsultant;

  const CARDS = [
    {
      ws: WORKSPACES.COMPANY,
      title: "Company",
      subtitle: "HR · Manager · HOD workflows",
      desc: "Manage vacancies, job posts, candidates, interviews, hiring flows and drives for your organisation.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect
            x="5"
            y="12"
            width="30"
            height="23"
            rx="2.5"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M14 12V9a6 6 0 0112 0v3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M5 22h30"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeDasharray="3 3"
          />
          <rect
            x="17"
            y="22"
            width="6"
            height="6"
            rx="1"
            fill="currentColor"
            opacity=".25"
          />
        </svg>
      ),
      gradient: "ws-card--company",
      badge: "HR Portal",
      disabled: isCompanyDisabled,
      upgrade: isCompanyUpgrade,
    },
    {
      ws: WORKSPACES.CONSULTANT,
      title: "Consultancy",
      subtitle: "Consultant · Admin workflows",
      desc: "Manage your consultancy's candidates, job posts, drives, connections and client requirements.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="15" r="7" stroke="currentColor" strokeWidth="2" />
          <path
            d="M6 35c0-7.732 6.268-14 14-14s14 6.268 14 14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M28 10l3-3m0 0l3-3m-3 3l3 3m-3-3l-3-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      ),
      gradient: "ws-card--consultant",
      badge: "Consultant Portal",
      disabled: isConsultantDisabled,
      upgrade: isConsultantUpgrade,
    },
  ];

  return (
    <div className="ws-page">
      {/* Background blobs */}
      <div className="ws-bg" aria-hidden="true">
        <div className="ws-bg__blob ws-bg__blob--1" />
        <div className="ws-bg__blob ws-bg__blob--2" />
      </div>

      {/* Header */}
      <div className="ws-header">
        <div className="ws-header__logo">
          <span className="ws-header__name">HRLabs</span>
        </div>
        <button className="ws-header__logout" onClick={logout}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path
              d="M6 2H3a1 1 0 00-1 1v9a1 1 0 001 1h3M10 10l3-3-3-3M13 7H6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Sign out
        </button>
      </div>

      {/* Content */}
      <div className="ws-content">
        <div className="ws-greeting">
          <h1 className="ws-greeting__title">
            Welcome back{user?.firstName ? `, ${user.firstName}` : ""}
          </h1>
          <p className="ws-greeting__sub">
            Choose how you'd like to work today
          </p>
        </div>

        {error && (
          <div className="ws-error">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle
                cx="8"
                cy="8"
                r="7"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M8 5v3.5M8 10.5v.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>{error}</span>
            <button className="ws-error__dismiss" onClick={() => setError("")}>
              Dismiss
            </button>
          </div>
        )}

        <div className="ws-cards">
          {CARDS.map((card) => {
            const locked = card.disabled;
            const upgrade = card.upgrade;
            return (
              <button
                key={card.ws}
                className={[
                  "ws-card",
                  card.gradient,
                  locked ? "ws-card--locked" : "",
                  upgrade ? "ws-card--upgrade" : "",
                  onlyOneAllowed && !locked && !upgrade
                    ? "ws-card--only-one"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => !locked && handleSelect(card.ws)}
                disabled={locked}
                title={
                  locked
                    ? "You don't have access to this workspace"
                    : upgrade
                      ? "Upgrade your plan to access this workspace"
                      : ""
                }
              >
                <div className="ws-card__badge">
                  {card.badge}

                  {locked && (
                    <span className="ws-card__lock ws-card__lock--red">
                      🔒 Locked
                    </span>
                  )}
                </div>

                <div className="ws-card__icon">{card.icon}</div>

                <div className="ws-card__body">
                  <h2 className="ws-card__title">{card.title}</h2>
                  <p className="ws-card__subtitle">{card.subtitle}</p>
                  <p className="ws-card__desc">{card.desc}</p>

                  {locked && (
                    <div className="ws-card__status ws-card__status--locked">
                      🔒 Access Restricted
                    </div>
                  )}

                  {upgrade && !locked && (
                    <div className="ws-card__status ws-card__status--upgrade">
                      🔴 Module Not Purchased
                    </div>
                  )}

                  {upgrade && !locked && (
                    <button
                      type="button"
                      className="ws-card__purchase-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          "https://ourdept.com",
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    >
                      🔒 Purchase Module
                    </button>
                  )}
                </div>

                <div className="ws-card__arrow">
                  {locked ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect
                        x="4"
                        y="10"
                        width="12"
                        height="8"
                        rx="1.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                      <path
                        d="M7 10V7a3 3 0 016 0v3"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : upgrade ? (
                    // External link / upgrade arrow
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 15L15 5M15 5H8M15 5v7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M4 10h12M11 5l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <p className="ws-footer-note">
          You can switch between workspaces anytime from the Navbar.
        </p>
      </div>
    </div>
  );
};

export default WorkspaceSelect;
